import type { FormVariant } from "@homeinstead/analytics";
import type { Rating as OfficeRating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type {
  CareRecipient,
  CareSeekerShallowLeadFormData,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroIconVariant } from "~www/types/national-hero";
import type { HeroVariant } from "../shared/hero-variants";

import { cn } from "@homeinstead/ui";
import { Rating } from "@homeinstead/ui/components/Rating";
import { RichText } from "@homeinstead/ui/components/RichText";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TrackedPhoneLink } from "~www/components/TrackedPhoneLink";
import { MINIMUM_RATING_TO_DISPLAY } from "~www/lib/office/constants";
import { isDisplayableRating } from "~www/lib/office/get-aggregate-rating";
import { CareRecipientButton } from "../shared/CareRecipientButton";
import { RecipientOptionsV1 } from "../shared/form-options";
import { getHeroVariantStyles } from "../shared/hero-variants";

interface CareRecipientStepProps {
  nextStep: () => void;
  className?: string;
  heroHeading: PortableTextBlock[];
  placeholders?: Record<string, string>;
  phoneNumber: string;
  officeRating?: OfficeRating;
  iconVariant?: HeroIconVariant;
  variant?: HeroVariant;
  formVariant: FormVariant;
}

const iconColorMap: Record<string, Record<number, string> | string> = {
  holiday: {
    0: "fill-[#EF5574] group-hover:fill-new-beginnings-base",
    1: "fill-[#82AFD7] group-hover:fill-new-beginnings-base",
    2: "fill-[#859A5E] group-hover:fill-new-beginnings-base",
    3: "fill-new-beginnings-base group-hover:fill-new-beginnings-base",
  },
  primary: "fill-friendly-base group-hover:fill-new-beginnings-base",
};

const CareRecipientStep = ({
  nextStep,
  className,
  heroHeading,
  placeholders,
  phoneNumber,
  officeRating,
  iconVariant = "primary",
  variant = "default",
  formVariant,
}: CareRecipientStepProps) => {
  const { control } = useFormContext<CareSeekerShallowLeadFormData>();

  const { textColorClass, recipientButtonVariant, linkVariant } =
    getHeroVariantStyles(variant);

  const canDisplayRating = isDisplayableRating(officeRating);

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-20 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-4 xl:items-start">
        <div
          className={cn(
            "text-left md:text-center xl:text-left [&_.text-md]:font-bold",
            textColorClass,
          )}
        >
          <RichText placeholders={placeholders} value={heroHeading} />
        </div>
        <p
          className={cn(
            "w-full text-left md:text-center xl:text-left",
            textColorClass,
          )}
        >
          {"I'm interested in home care services for:"}
        </p>
        <Controller
          control={control}
          name="careFor"
          render={({ field }) => (
            <div className="flex w-full flex-col gap-4 md:w-[440px] md:gap-3">
              <input type="hidden" {...field} />
              {RecipientOptionsV1.map((option, idx) => {
                const Icon = option.icon;

                const optionFill =
                  iconVariant === "holiday"
                    ? iconColorMap.holiday[idx]
                    : iconColorMap[iconVariant];
                return (
                  <CareRecipientButton
                    buttonVariant={recipientButtonVariant}
                    careRecipient={option.value as CareRecipient}
                    className="group flex h-12 w-full justify-start gap-4 rounded-full"
                    field={field}
                    formVariant={formVariant}
                    key={option.value}
                    nextStepCallback={nextStep}
                  >
                    <Icon className={cn("size-6", optionFill)} />
                    <span>{option.label}</span>
                  </CareRecipientButton>
                );
              })}
              {canDisplayRating ? (
                <Rating
                  className="self-center xl:mt-3"
                  minimumRating={MINIMUM_RATING_TO_DISPLAY}
                  rating={officeRating.rating}
                  ratingCount={officeRating.ratingCount}
                  service={officeRating.ratingService}
                  variant={variant === "light" ? "light" : "default"}
                />
              ) : null}
              {/* Button hidden on desktop and moved underneath the hero image */}
              <TrackedPhoneLink
                className="mt-4 flex h-12 justify-center gap-4 px-6"
                phoneNumber={phoneNumber}
                placement="lead form"
                variant={linkVariant}
                withIcon
              ></TrackedPhoneLink>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default CareRecipientStep;
